/**
 * geo.ts — Geographische Berechnungen (Distanz, Koordinaten).
 */

/**
 * Ein geografischer Punkt (WGS84).
 * Wird von {@link routeDistanz} und von `co2.ts` wiederverwendet.
 */
export interface GeoPoint {
  lat: number;
  lng: number;
}

/**
 * Rundet eine Position auf 2 Nachkommastellen (≈ 1 km) — für Positionen,
 * die gespeichert werden, aber nur grob gebraucht werden (11.10.2026).
 *
 * Gemerkte Strecken (Freie Rückfahrten) brauchen keine Hausnummer-genaue
 * Position: Der Abgleich arbeitet mit mindestens 10 km Umkreis, der Punkt
 * verschiebt sich höchstens um etwa 600 m. Datensparsamkeit (Art. 5 DSGVO).
 *
 * @param p Position
 * @returns neue Position mit gerundeten Werten
 */
export function grobePosition(p: GeoPoint): GeoPoint {
  return { lat: Math.round(p.lat * 100) / 100, lng: Math.round(p.lng * 100) / 100 };
}

/**
 * Shape eines Objekts, das von {@link routeDistanz} akzeptiert wird.
 * Alle Felder optional — die Funktion liefert `null`, wenn Abhol-
 * oder Zielkoordinaten fehlen.
 */
export interface RouteDistanzInput {
  geoAbhol?: GeoPoint | null;
  geoZiel?: GeoPoint | null;
  _geo?: GeoPoint | null;
  _geoZiel?: GeoPoint | null;
}

/**
 * Berechnet die Entfernung zwischen zwei GPS-Koordinaten (Haversine-Formel).
 *
 * @param lat1 - Breitengrad Punkt 1
 * @param lon1 - Längengrad Punkt 1
 * @param lat2 - Breitengrad Punkt 2
 * @param lon2 - Längengrad Punkt 2
 * @returns Distanz in Kilometern
 */
export function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.pow(Math.sin(dLat / 2), 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.pow(Math.sin(dLon / 2), 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Berechnet die ungefähre Fahrt-Distanz eines Auftrags über Haversine
 * (Luftlinie) plus 30 % Aufschlag als grobe Straßen-Approximation.
 * Liefert `null`, wenn Abhol- oder Ziel-Koordinaten fehlen.
 *
 * @param a - Auftrag mit `geoAbhol`/`geoZiel` oder `_geo`/`_geoZiel`.
 * @returns Geschätzte Kilometer oder `null`.
 */
export function routeDistanz(a: RouteDistanzInput): number | null {
  const from = a.geoAbhol || a._geo;
  const to = a.geoZiel || a._geoZiel;
  if (!from || !to) return null;
  return Math.round(haversine(from.lat, from.lng, to.lat, to.lng) * 1.3);
}

/**
 * Prüft, ob ein Punkt `pruefPunkt` "auf dem Weg" zwischen `start` und `ziel`
 * liegt — d.h. ob der Umweg über den Punkt den Toleranzwert nicht überschreitet.
 *
 * Berechnung:
 *   - Direkte Distanz Start→Ziel
 *   - Umweg-Distanz Start→Punkt→Ziel
 *   - Umweg = Differenz beider
 *   - Erlaubter Umweg = max(Toleranz × DirekteDistanz, 30 km Mindest-Toleranz)
 *
 * Die Mindest-Toleranz von 30 km verhindert, dass bei sehr kurzen Strecken
 * (z.B. 50 km) ein vernünftiger Umweg von 20-25 km schon "zu viel" ist.
 *
 * @param start - Startpunkt (z.B. aktueller Standort oder Ziel-Stadt der akzept. Lieferung)
 * @param ziel - Endpunkt (z.B. Heimat-Geo)
 * @param pruefPunkt - Punkt, der auf "im Korridor" geprüft wird (z.B. Abholort einer Rückfahrt)
 * @param toleranz - Anteil 0..1 (Default 0.3 = 30%)
 * @returns true wenn pruefPunkt im Korridor liegt, sonst false.
 */
export function istImKorridor(
  start: GeoPoint,
  ziel: GeoPoint,
  pruefPunkt: GeoPoint,
  toleranz = 0.3,
): boolean {
  const direkt = haversine(start.lat, start.lng, ziel.lat, ziel.lng);
  const ueberPunkt =
    haversine(start.lat, start.lng, pruefPunkt.lat, pruefPunkt.lng) +
    haversine(pruefPunkt.lat, pruefPunkt.lng, ziel.lat, ziel.lng);
  const umweg = ueberPunkt - direkt;
  const maxUmweg = Math.max(direkt * toleranz, 30);
  return umweg <= maxUmweg;
}

/** Antwort von Nominatim „reverse" — nur die Felder, die wir nutzen. */
export interface NominatimReverse {
  display_name?: string;
  address?: Record<string, string | undefined>;
}

/**
 * Fragt Nominatim nach der Adresse zu Koordinaten.
 *
 * @returns Antwort oder null, wenn die Abfrage scheitert
 */
async function nominatimReverse(lat: number, lng: number): Promise<NominatimReverse | null> {
  try {
    const r = await fetch(
      'https://nominatim.openstreetmap.org/reverse?lat=' + lat + '&lon=' + lng + '&format=json&accept-language=de',
    );
    return (await r.json()) as NominatimReverse;
  } catch {
    return null;
  }
}

/**
 * Nur der Ortsname aus einer Nominatim-Antwort — ohne Straße und
 * Hausnummer. Reine Funktion, damit testbar.
 *
 * Nominatim liefert je nach Größe `city`, `town`, `village` usw.; die
 * Reihenfolge geht vom großen zum kleinen Ort.
 *
 * @returns Ortsname oder null
 */
export function ortAusNominatim(data: NominatimReverse | null): string | null {
  const a = data?.address;
  if (!a) return null;
  const ort = a.city || a.town || a.village || a.municipality || a.hamlet || a.suburb || '';
  return ort.trim() || null;
}

/**
 * Ortsname zu Koordinaten über OpenStreetMap (Nominatim, „reverse").
 *
 * Liefert die ersten drei Teile der Adresse, z. B.
 * „12, Erbesgasse, Beienheim". Genutzt vom GPS-Knopf im Marktplatz, wo
 * die Straße für den eigenen Standort hilfreich ist.
 *
 * Für die Heimat / das Einsatzgebiet stattdessen {@link ortAusKoordinaten}
 * verwenden — dort gehört keine Wohnadresse hin.
 *
 * @param lat Breitengrad
 * @param lng Längengrad
 * @returns Ortsname oder null, wenn die Abfrage scheitert
 */
export async function ortsnameAusKoordinaten(lat: number, lng: number): Promise<string | null> {
  const data = await nominatimReverse(lat, lng);
  if (!data || !data.display_name) return null;
  return data.display_name.split(',').slice(0, 3).join(',').trim() || null;
}

/**
 * Nur der Ort zu Koordinaten (ohne Straße), z. B. „Beienheim".
 * Für Einsatzgebiet und Rückfahrt-Ziel (07.10.2026).
 *
 * @returns Ortsname oder null, wenn die Abfrage scheitert
 */
export async function ortAusKoordinaten(lat: number, lng: number): Promise<string | null> {
  return ortAusNominatim(await nominatimReverse(lat, lng));
}

// ═══════════════════════════════════════════════════════════════
// Koordinaten zu einer Adresse (21.10.2026, gescannte Adressen)
// ═══════════════════════════════════════════════════════════════

/** Länder, in denen gesucht wird — dieselbe Liste wie die Adresssuche (Europa). */
const GEO_LAENDER =
  'de,at,ch,pl,nl,be,fr,cz,lu,dk,se,no,fi,it,es,pt,gb,ie,hr,si,sk,hu,ro,bg,gr,ee,lv,lt,rs,ba,me,mk,al,xk,md,ua,by,is,mt,cy';

/** Ländernamen, die am Ende einer Adresse stehen können (kein Teil der Suche). */
const LAND_AM_ENDE =
  /^(deutschland|germany|nederland|niederlande|netherlands|österreich|austria|schweiz|switzerland|polska|polen|poland|france|frankreich|italia|italien|italy)$/i;

/**
 * Suchtexte für eine Adresse der Form „Firma, Straße Nr, PLZ Ort",
 * so wie sie der Frachtbrief-Scan liefert. Nominatim findet mit
 * Firmennamen oft nichts, deshalb in dieser Reihenfolge:
 *   1. ohne den ersten Teil (Firma) — „Straße Nr, PLZ Ort"
 *   2. die ganze Adresse
 *   3. nur der letzte Teil mit Ziffern — „PLZ Ort"
 * Ein Land am Ende („Deutschland") wird entfernt. Reine Funktion.
 *
 * @param adresse Adresse als eine Zeile, Teile mit Komma getrennt
 * @returns Suchtexte ohne Duplikate, bestmögliche zuerst; leer bei leerer Adresse
 */
export function suchTexteFuerAdresse(adresse: string): string[] {
  let teile = String(adresse || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  if (teile.length >= 2 && LAND_AM_ENDE.test(teile[teile.length - 1])) {
    teile = teile.slice(0, -1);
  }
  if (teile.length === 0) return [];
  const texte: string[] = [];
  if (teile.length >= 3) texte.push(teile.slice(1).join(', '));
  texte.push(teile.join(', '));
  const letzter = teile[teile.length - 1];
  if (teile.length >= 2 && /\d/.test(letzter)) texte.push(letzter);
  return texte.filter((t, i) => texte.indexOf(t) === i);
}

/** Pause zwischen zwei Nominatim-Anfragen (Nutzungsrichtlinie: höchstens 1 pro Sekunde). */
const GEO_PAUSE_MS = 1100;
/** Abbruch einer einzelnen Anfrage (ms). */
const GEO_TIMEOUT_MS = 6000;

/**
 * Koordinaten zu einer Adresse über OpenStreetMap (Nominatim, nur Europa).
 *
 * Probiert die Suchtexte aus {@link suchTexteFuerAdresse} nacheinander und
 * nimmt den ersten Treffer. Scheitert alles (Netz, kein Treffer, außerhalb
 * Europas), kommt `null` — der Aufrufer macht dann ohne Koordinaten weiter.
 *
 * @param adresse Adresse als eine Zeile
 * @returns Position oder null
 */
export async function geocodeAdresse(adresse: string): Promise<GeoPoint | null> {
  const texte = suchTexteFuerAdresse(adresse);
  for (let i = 0; i < texte.length; i++) {
    if (i > 0) await new Promise<void>((r) => setTimeout(r, GEO_PAUSE_MS));
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), GEO_TIMEOUT_MS);
    try {
      const resp = await fetch(
        'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=' +
          GEO_LAENDER +
          '&q=' +
          encodeURIComponent(texte[i]),
        { signal: ctrl.signal },
      );
      const data = (await resp.json()) as Array<{ lat?: string; lon?: string }>;
      const lat = parseFloat(data?.[0]?.lat ?? '');
      const lng = parseFloat(data?.[0]?.lon ?? '');
      if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= 34 && lat <= 72 && lng >= -12 && lng <= 45) {
        return { lat, lng };
      }
    } catch {
      /* nächster Suchtext */
    } finally {
      clearTimeout(timer);
    }
  }
  return null;
}
